import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncIcon from "@mui/icons-material/Sync";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import { fmtDate } from "../format";
import { useToast } from "../toast";
import type {
  RoamingCdrDelivery,
  RoamingDispute,
  RoamingOverview,
  RoamingPartner,
  RoamingPartnerWithToken,
  RoamingSettlement,
  RoamingTokenSyncResult,
} from "../types";

interface PartnerDraft {
  name: string;
  country_code: string;
  party_id: string;
  versions_endpoint_url: string;
  credentials_endpoint_url: string;
  cdrs_endpoint_url: string;
  outbound_token: string;
}

const EMPTY_DRAFT: PartnerDraft = {
  name: "",
  country_code: "",
  party_id: "",
  versions_endpoint_url: "",
  credentials_endpoint_url: "",
  cdrs_endpoint_url: "",
  outbound_token: "",
};

export default function Roaming() {
  const api = useAuthApi();
  const toast = useToast();

  const [items, setItems] = useState<RoamingPartner[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [rotatingId, setRotatingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [selectedPartner, setSelectedPartner] = useState<RoamingPartner | null>(null);
  const [partnerCdrs, setPartnerCdrs] = useState<RoamingCdrDelivery[]>([]);
  const [partnerSettlements, setPartnerSettlements] = useState<RoamingSettlement[]>([]);
  const [partnerDisputes, setPartnerDisputes] = useState<RoamingDispute[]>([]);
  const [overview, setOverview] = useState<RoamingOverview | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [settlementActionId, setSettlementActionId] = useState<number | null>(null);
  const [draft, setDraft] = useState<PartnerDraft>(EMPTY_DRAFT);
  const [search, setSearch] = useState("");
  const [tokenData, setTokenData] = useState<RoamingPartnerWithToken | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api<RoamingPartner[]>(`${MGMT}/roaming/partners`);
      setItems(data ?? []);
      const summary = await api<RoamingOverview>(`${MGMT}/roaming/partners/overview`);
      setOverview(summary ?? null);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [api, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDraftField =
    (field: keyof PartnerDraft) => (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (field === "country_code") {
        setDraft((prev) => ({ ...prev, country_code: value.toUpperCase().slice(0, 2) }));
        return;
      }
      if (field === "party_id") {
        setDraft((prev) => ({ ...prev, party_id: value.toUpperCase().slice(0, 3) }));
        return;
      }
      setDraft((prev) => ({ ...prev, [field]: value }));
    };

  const canCreate = useMemo(
    () =>
      draft.name.trim().length > 0
      && draft.country_code.length === 2
      && draft.party_id.length === 3,
    [draft]
  );

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      const created = await api<RoamingPartnerWithToken>(`${MGMT}/roaming/partners`, {
        method: "POST",
        body: JSON.stringify({
          name: draft.name.trim(),
          country_code: draft.country_code,
          party_id: draft.party_id,
          versions_endpoint_url: draft.versions_endpoint_url.trim() || undefined,
          credentials_endpoint_url: draft.credentials_endpoint_url.trim() || undefined,
          cdrs_endpoint_url: draft.cdrs_endpoint_url.trim() || undefined,
          outbound_token: draft.outbound_token.trim() || undefined,
        }),
      });
      if (created) {
        setTokenData(created);
        setCreateOpen(false);
        setDraft(EMPTY_DRAFT);
        toast.show("Roaming partner created");
        await load();
      }
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setCreating(false);
    }
  };

  const handleRotateToken = async (partner: RoamingPartner) => {
    setRotatingId(partner.id);
    try {
      const rotated = await api<RoamingPartnerWithToken>(
        `${MGMT}/roaming/partners/${partner.id}/rotate-token`,
        { method: "POST" }
      );
      if (rotated) {
        setTokenData(rotated);
        toast.show(`Token rotated for ${partner.name}`);
      }
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setRotatingId(null);
    }
  };

  const handleDeletePartner = async (partner: RoamingPartner) => {
    setDeletingId(partner.id);
    try {
      await api(`${MGMT}/roaming/partners/${partner.id}`, { method: "DELETE" });
      toast.show(`Deleted ${partner.name}`);
      await load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setDeletingId(null);
    }
  };

  const openPartnerDetails = async (partner: RoamingPartner) => {
    setSelectedPartner(partner);
    setPartnerCdrs([]);
    setPartnerSettlements([]);
    setPartnerDisputes([]);
    setDetailsLoading(true);
    try {
      const [cdrData, settlementData, disputeData] = await Promise.all([
        api<RoamingCdrDelivery[]>(`${MGMT}/roaming/partners/${partner.id}/cdrs`),
        api<RoamingSettlement[]>(`${MGMT}/roaming/partners/${partner.id}/settlements`),
        api<RoamingDispute[]>(`${MGMT}/roaming/partners/${partner.id}/disputes`),
      ]);
      setPartnerCdrs(cdrData ?? []);
      setPartnerSettlements(settlementData ?? []);
      setPartnerDisputes(disputeData ?? []);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSyncTokens = async (partner: RoamingPartner) => {
    setSyncingId(partner.id);
    try {
      const result = await api<RoamingTokenSyncResult>(`${MGMT}/roaming/partners/${partner.id}/tokens/sync`, {
        method: "POST",
      });
      toast.show(`Synced ${result?.count ?? 0} tokens for ${partner.name}`);
      await load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSyncingId(null);
    }
  };

  const handleRetryCdrs = async (partner: RoamingPartner) => {
    setRetryingId(partner.id);
    try {
      const result = await api<{ retried: number }>(`${MGMT}/roaming/partners/${partner.id}/cdrs/retry`, {
        method: "POST",
      });
      toast.show(`Queued ${result?.retried ?? 0} CDRs for retry`);
      await openPartnerDetails(partner);
      await load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setRetryingId(null);
    }
  };

  const handleGenerateSettlements = async (partner: RoamingPartner) => {
    setSettlementActionId(partner.id);
    try {
      await api(`${MGMT}/roaming/partners/${partner.id}/settlements/generate`, { method: "POST" });
      toast.show(`Generated settlement run for ${partner.name}`);
      await openPartnerDetails(partner);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSettlementActionId(null);
    }
  };

  const handleCloseSettlement = async (partner: RoamingPartner, settlement: RoamingSettlement) => {
    setSettlementActionId(settlement.id);
    try {
      await api(`${MGMT}/roaming/partners/settlements/${settlement.id}/close`, { method: "POST" });
      toast.show(`Closed settlement ${settlement.invoice_number ?? settlement.id}`);
      await openPartnerDetails(partner);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSettlementActionId(null);
    }
  };

  const handleReconcileSettlement = async (partner: RoamingPartner, settlement: RoamingSettlement) => {
    setSettlementActionId(settlement.id);
    try {
      await api(`${MGMT}/roaming/partners/settlements/${settlement.id}/reconcile`, {
        method: "POST",
        body: JSON.stringify({ payment_reference: `manual-${settlement.id}` }),
      });
      toast.show(`Reconciled settlement ${settlement.invoice_number ?? settlement.id}`);
      await openPartnerDetails(partner);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSettlementActionId(null);
    }
  };

  const handleCreateDispute = async (partner: RoamingPartner, settlement: RoamingSettlement) => {
    setSettlementActionId(settlement.id);
    try {
      await api(`${MGMT}/roaming/partners/${partner.id}/disputes`, {
        method: "POST",
        body: JSON.stringify({
          settlement_id: settlement.id,
          reason: `Review settlement ${settlement.invoice_number ?? settlement.id}`,
          evidence: `Amount ${settlement.amount} ${settlement.currency}`,
        }),
      });
      toast.show("Dispute opened");
      await openPartnerDetails(partner);
      await load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSettlementActionId(null);
    }
  };

  const handleResolveDispute = async (partner: RoamingPartner, dispute: RoamingDispute) => {
    setSettlementActionId(dispute.id);
    try {
      await api(`${MGMT}/roaming/partners/disputes/${dispute.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          status: "resolved",
          resolution_notes: "Resolved by operator",
        }),
      });
      toast.show(`Dispute ${dispute.id} resolved`);
      await openPartnerDetails(partner);
      await load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSettlementActionId(null);
    }
  };

  const copyToClipboard = async (value: string) => {
    await navigator.clipboard.writeText(value);
    toast.show("Copied to clipboard");
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter((partner) => {
      const haystack = [
        partner.name,
        partner.country_code,
        partner.party_id,
        partner.status,
        partner.is_active ? "active" : "inactive",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [items, search]);

  return (
    <>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }} alignItems="center">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New Roaming Partner
        </Button>
        <TextField
          size="small"
          placeholder="Filter roaming partners…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        Add partner CPOs/eMSPs to issue inbound OCPI credentials and manage token rotation securely.
      </Alert>

      {overview && (
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }}>
          <Card variant="outlined" sx={{ minWidth: 170 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Partners</Typography>
              <Typography variant="h6">{overview.partners_active}</Typography>
              <Typography variant="caption" color="error.main">{overview.partners_with_errors} with errors</Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ minWidth: 170 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Tokens</Typography>
              <Typography variant="h6">{overview.tokens_total}</Typography>
              <Typography variant="caption" color="warning.main">{overview.tokens_revoked} revoked</Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ minWidth: 200 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">CDR Queue</Typography>
              <Typography variant="h6">{overview.cdr_pending} pending</Typography>
              <Typography variant="caption" color="error.main">
                {overview.cdr_failed} failed / {overview.cdr_dead_letter} dead-letter
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ minWidth: 220 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Settlements</Typography>
              <Typography variant="h6">{overview.settlements_open} open</Typography>
              <Typography variant="caption" color="text.secondary">
                {overview.settlements_invoiced} invoiced / {overview.settlements_paid} paid • {overview.disputes_open} disputes
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Party</TableCell>
              <TableCell>Lifecycle</TableCell>
              <TableCell>Sync</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      {search.trim()
                        ? "No roaming partners match the current filter."
                        : "No roaming partners yet. Create the first connection to start OCPI roaming."}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((partner) => (
              <TableRow key={partner.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{partner.name}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {partner.country_code}-{partner.party_id}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip size="small" label={partner.status} color={partner.is_active ? "success" : "default"} />
                    {partner.last_error ? <Chip size="small" label="Issues" color="error" /> : null}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">
                      {partner.last_token_sync_at ? `Last sync ${fmtDate(partner.last_token_sync_at)}` : "Not synced"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {partner.last_success_at ? `Last success ${fmtDate(partner.last_success_at)}` : "No successful calls"}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={partner.is_active ? "Active" : "Inactive"}
                    color={partner.is_active ? "success" : "default"}
                  />
                </TableCell>
                <TableCell>{fmtDate(partner.created_at)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Rotate inbound token">
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleRotateToken(partner)}
                        disabled={rotatingId === partner.id || deletingId === partner.id}
                      >
                        {rotatingId === partner.id ? <CircularProgress size={18} /> : <SyncIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Sync tokens">
                    <span>
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleSyncTokens(partner)}
                        disabled={syncingId === partner.id || retryingId === partner.id}
                      >
                        {syncingId === partner.id ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Inspect delivery queue">
                    <span>
                      <IconButton size="small" onClick={() => openPartnerDetails(partner)}>
                        <DescriptionIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Retry CDRs">
                    <span>
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => handleRetryCdrs(partner)}
                        disabled={retryingId === partner.id || syncingId === partner.id}
                      >
                        {retryingId === partner.id ? <CircularProgress size={18} /> : <SyncIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete partner">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeletePartner(partner)}
                        disabled={deletingId === partner.id || rotatingId === partner.id}
                      >
                        {deletingId === partner.id
                          ? <CircularProgress size={18} />
                          : <DeleteIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Roaming Partner</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="Partner name"
              value={draft.name}
              onChange={handleDraftField("name")}
              placeholder="e.g. FastCharge Network"
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Country code"
                value={draft.country_code}
                onChange={handleDraftField("country_code")}
                placeholder="DE"
                helperText="2 characters (ISO 3166-1 alpha-2)"
                inputProps={{ maxLength: 2 }}
                fullWidth
              />
              <TextField
                label="Party ID"
                value={draft.party_id}
                onChange={handleDraftField("party_id")}
                placeholder="CHB"
                helperText="3 characters"
                inputProps={{ maxLength: 3 }}
                fullWidth
              />
            </Stack>
            <TextField
              label="Partner versions URL"
              value={draft.versions_endpoint_url}
              onChange={handleDraftField("versions_endpoint_url")}
              placeholder="https://partner.example/ocpi/versions"
              fullWidth
            />
            <TextField
              label="Partner credentials URL"
              value={draft.credentials_endpoint_url}
              onChange={handleDraftField("credentials_endpoint_url")}
              placeholder="https://partner.example/ocpi/2.2.1/credentials"
              fullWidth
            />
            <TextField
              label="Partner CDR URL"
              value={draft.cdrs_endpoint_url}
              onChange={handleDraftField("cdrs_endpoint_url")}
              placeholder="https://partner.example/ocpi/cdrs"
              fullWidth
            />
            <TextField
              label="Outbound token"
              value={draft.outbound_token}
              onChange={handleDraftField("outbound_token")}
              placeholder="Partner auth token"
              helperText="Optional token used when pushing CDRs to the partner."
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!canCreate || creating}>
            {creating ? <CircularProgress size={18} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={selectedPartner !== null}
        onClose={() => setSelectedPartner(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Roaming Partner Details</DialogTitle>
        <DialogContent>
          {selectedPartner && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
                <Chip label={selectedPartner.status} color={selectedPartner.is_active ? "success" : "default"} />
                <Chip label={selectedPartner.country_code + "-" + selectedPartner.party_id} variant="outlined" />
                {selectedPartner.last_error ? <Chip label={selectedPartner.last_error} color="error" /> : null}
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Typography variant="body2">Handshake: {selectedPartner.last_handshake_at ? fmtDate(selectedPartner.last_handshake_at) : "—"}</Typography>
                <Typography variant="body2">Token sync: {selectedPartner.last_token_sync_at ? fmtDate(selectedPartner.last_token_sync_at) : "—"}</Typography>
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Typography variant="body2">Last success: {selectedPartner.last_success_at ? fmtDate(selectedPartner.last_success_at) : "—"}</Typography>
                <Typography variant="body2">Last failure: {selectedPartner.last_failure_at ? fmtDate(selectedPartner.last_failure_at) : "—"}</Typography>
              </Stack>
              <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                Versions URL: {selectedPartner.versions_endpoint_url || "—"}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                Credentials URL: {selectedPartner.credentials_endpoint_url || "—"}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                CDR URL: {selectedPartner.cdrs_endpoint_url || "—"}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button variant="outlined" onClick={() => handleSyncTokens(selectedPartner)} disabled={syncingId === selectedPartner.id}>
                  Sync tokens
                </Button>
                <Button variant="outlined" onClick={() => handleRetryCdrs(selectedPartner)} disabled={retryingId === selectedPartner.id}>
                  Retry CDRs
                </Button>
                <Button variant="outlined" onClick={() => handleRotateToken(selectedPartner)} disabled={rotatingId === selectedPartner.id}>
                  Rotate token
                </Button>
                <Button variant="outlined" onClick={() => handleGenerateSettlements(selectedPartner)} disabled={settlementActionId === selectedPartner.id}>
                  Generate settlements
                </Button>
              </Stack>
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Delivery queue
                </Typography>
                {detailsLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>CDR</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Attempts</TableCell>
                          <TableCell>Next retry</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {partnerCdrs.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              No CDR deliveries yet.
                            </TableCell>
                          </TableRow>
                        ) : (
                          partnerCdrs.map((cdr) => (
                            <TableRow key={cdr.id}>
                              <TableCell sx={{ fontFamily: "monospace" }}>{cdr.cdr_id}</TableCell>
                              <TableCell>{cdr.status}</TableCell>
                              <TableCell>{cdr.attempt_count}</TableCell>
                              <TableCell>{cdr.next_retry_at ? fmtDate(cdr.next_retry_at) : "—"}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Settlements
                </Typography>
                {detailsLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Invoice</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Period</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {partnerSettlements.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              No settlements yet.
                            </TableCell>
                          </TableRow>
                        ) : (
                          partnerSettlements.map((settlement) => (
                            <TableRow key={settlement.id}>
                              <TableCell sx={{ fontFamily: "monospace" }}>{settlement.invoice_number ?? settlement.id}</TableCell>
                              <TableCell>{settlement.status}</TableCell>
                              <TableCell>{settlement.amount} {settlement.currency}</TableCell>
                              <TableCell>
                                {settlement.period_start ? fmtDate(settlement.period_start) : "—"} - {settlement.period_end ? fmtDate(settlement.period_end) : "—"}
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleCloseSettlement(selectedPartner, settlement)}
                                    disabled={settlementActionId === settlement.id}
                                  >
                                    Close
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => handleReconcileSettlement(selectedPartner, settlement)}
                                    disabled={settlementActionId === settlement.id}
                                  >
                                    Reconcile
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="text"
                                    color="warning"
                                    onClick={() => handleCreateDispute(selectedPartner, settlement)}
                                    disabled={settlementActionId === settlement.id}
                                  >
                                    Dispute
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Disputes
                </Typography>
                {detailsLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Reason</TableCell>
                          <TableCell>Opened</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {partnerDisputes.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              No disputes.
                            </TableCell>
                          </TableRow>
                        ) : (
                          partnerDisputes.map((dispute) => (
                            <TableRow key={dispute.id}>
                              <TableCell>{dispute.id}</TableCell>
                              <TableCell>{dispute.status}</TableCell>
                              <TableCell>{dispute.reason}</TableCell>
                              <TableCell>{fmtDate(dispute.opened_at)}</TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleResolveDispute(selectedPartner, dispute)}
                                  disabled={settlementActionId === dispute.id || dispute.status === "resolved"}
                                >
                                  Resolve
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedPartner(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={tokenData !== null}
        onClose={() => setTokenData(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Inbound Token</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Store this token now. It will not be shown again.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Partner: <strong>{tokenData?.name}</strong> ({tokenData?.country_code}-{tokenData?.party_id})
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              sx={{
                fontFamily: "monospace",
                bgcolor: "action.hover",
                p: 1.5,
                borderRadius: 1,
                flexGrow: 1,
                wordBreak: "break-all",
              }}
            >
              {tokenData?.inbound_token}
            </Typography>
            <Tooltip title="Copy">
              <IconButton onClick={() => tokenData?.inbound_token && copyToClipboard(tokenData.inbound_token)}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setTokenData(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
